// Sign-in screen: email + password against the firm's Cognito pool.
// Invited users arrive with a temporary password and set their own here.

import { useState } from "react";
import { useAuth } from "../AuthContext";
import { confirmPasswordReset, requestPasswordReset } from "../auth";
import { Btn } from "../components/Primitives";
import { Mark } from "../components/Icon";

type Step = "signin" | "new-password" | "reset-request" | "reset-confirm";

export function Login() {
  const { signIn, completeNewPassword } = useAuth();
  const [step, setStep] = useState<Step>("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPw, setNewPw] = useState("");
  const [newPw2, setNewPw2] = useState("");
  const [code, setCode] = useState("");
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submitSignIn = async () => {
    if (!email.trim() || !password || busy) return;
    setBusy(true);
    setError(null);
    try {
      if ((await signIn(email, password)) === "new-password") setStep("new-password");
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async () => {
    if (busy) return;
    if (newPw.length < 12) { setError("Password must be at least 12 characters."); return; }
    if (newPw !== newPw2) { setError("Passwords don't match."); return; }
    setBusy(true);
    setError(null);
    try {
      await completeNewPassword(newPw);
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  const submitResetRequest = async () => {
    if (!email.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email);
      setStep("reset-confirm");
      setNotice(`If an account exists for ${email.trim()}, a reset code is on its way.`);
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  const submitResetConfirm = async () => {
    if (busy) return;
    if (!code.trim()) { setError("Enter the code from the email."); return; }
    if (newPw.length < 12) { setError("Password must be at least 12 characters."); return; }
    if (newPw !== newPw2) { setError("Passwords don't match."); return; }
    setBusy(true);
    setError(null);
    try {
      await confirmPasswordReset(email, code, newPw);
      setStep("signin");
      setPassword("");
      setNewPw(""); setNewPw2(""); setCode("");
      setNotice("Password updated — sign in with your new password.");
    } catch (e) {
      setError(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  const onEnter = (fn: () => void) => (e: React.KeyboardEvent) => {
    if (e.key === "Enter") fn();
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "var(--bg)",
      zIndex: 100, display: "grid", placeItems: "center",
    }}>
      <div style={{ width: 360 }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          justifyContent: "center", marginBottom: 36,
        }}>
          <Mark size={26} />
          <span style={{
            fontWeight: 600, fontSize: 20, letterSpacing: "-0.015em",
          }}>Prism</span>
        </div>
        <div style={{
          background: "var(--surface)", border: "1px solid var(--border)",
          borderRadius: 10, padding: 24,
        }}>
          {step === "signin" && (
            <>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Sign in</div>
              <div style={{ color: "var(--ink-3)", fontSize: 12.5, marginTop: 3 }}>
                Use your firm's account. We'll match you to your seat.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 18 }}>
                <div className="input">
                  <input
                    placeholder="you@firm.vc" type="email" autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={onEnter(submitSignIn)} autoComplete="username"
                  />
                </div>
                <div className="input">
                  <input
                    placeholder="Password" type="password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={onEnter(submitSignIn)} autoComplete="current-password"
                  />
                </div>
              </div>
              <Btn
                kind="primary" disabled={busy}
                style={{ width: "100%", marginTop: 14, justifyContent: "center" }}
                onClick={submitSignIn}
              >{busy ? "Signing in…" : "Sign in"}</Btn>
              <div
                style={{
                  marginTop: 12, textAlign: "center", fontSize: 12,
                  color: "var(--ink-3)", cursor: "pointer",
                  textDecoration: "underline", textUnderlineOffset: 2,
                }}
                onClick={() => { setError(null); setNotice(null); setStep("reset-request"); }}
              >Forgot password?</div>
            </>
          )}
          {step === "reset-request" && (
            <>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Reset password</div>
              <div style={{ color: "var(--ink-3)", fontSize: 12.5, marginTop: 3 }}>
                We'll email you a reset code.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 18 }}>
                <div className="input">
                  <input
                    placeholder="you@firm.vc" type="email" autoFocus
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={onEnter(submitResetRequest)} autoComplete="username"
                  />
                </div>
              </div>
              <Btn
                kind="primary" disabled={busy}
                style={{ width: "100%", marginTop: 14, justifyContent: "center" }}
                onClick={submitResetRequest}
              >{busy ? "Sending…" : "Send reset code"}</Btn>
              <div
                style={{
                  marginTop: 12, textAlign: "center", fontSize: 12,
                  color: "var(--ink-3)", cursor: "pointer",
                }}
                onClick={() => { setError(null); setStep("signin"); }}
              >Back to sign in</div>
            </>
          )}
          {step === "reset-confirm" && (
            <>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Enter the code</div>
              <div style={{ color: "var(--ink-3)", fontSize: 12.5, marginTop: 3 }}>
                Check {email.trim() || "your email"} for the reset code, then
                choose a new password (at least 12 characters).
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 18 }}>
                <div className="input">
                  <input
                    placeholder="Reset code" autoFocus inputMode="numeric"
                    value={code} onChange={(e) => setCode(e.target.value)}
                    onKeyDown={onEnter(submitResetConfirm)} autoComplete="one-time-code"
                  />
                </div>
                <div className="input">
                  <input
                    placeholder="New password" type="password"
                    value={newPw} onChange={(e) => setNewPw(e.target.value)}
                    onKeyDown={onEnter(submitResetConfirm)} autoComplete="new-password"
                  />
                </div>
                <div className="input">
                  <input
                    placeholder="Repeat new password" type="password"
                    value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
                    onKeyDown={onEnter(submitResetConfirm)} autoComplete="new-password"
                  />
                </div>
              </div>
              <Btn
                kind="primary" disabled={busy}
                style={{ width: "100%", marginTop: 14, justifyContent: "center" }}
                onClick={submitResetConfirm}
              >{busy ? "Saving…" : "Set new password"}</Btn>
              <div
                style={{
                  marginTop: 12, textAlign: "center", fontSize: 12,
                  color: "var(--ink-3)", cursor: "pointer",
                }}
                onClick={() => { setError(null); setStep("signin"); }}
              >Back to sign in</div>
            </>
          )}
          {step === "new-password" && (
            <>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Choose your password</div>
              <div style={{ color: "var(--ink-3)", fontSize: 12.5, marginTop: 3 }}>
                Your temporary password worked. Pick your own to finish
                setting up — at least 12 characters.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 18 }}>
                <div className="input">
                  <input
                    placeholder="New password" type="password" autoFocus
                    value={newPw} onChange={(e) => setNewPw(e.target.value)}
                    onKeyDown={onEnter(submitNewPassword)} autoComplete="new-password"
                  />
                </div>
                <div className="input">
                  <input
                    placeholder="Repeat new password" type="password"
                    value={newPw2} onChange={(e) => setNewPw2(e.target.value)}
                    onKeyDown={onEnter(submitNewPassword)} autoComplete="new-password"
                  />
                </div>
              </div>
              <Btn
                kind="primary" disabled={busy}
                style={{ width: "100%", marginTop: 14, justifyContent: "center" }}
                onClick={submitNewPassword}
              >{busy ? "Saving…" : "Set password & sign in"}</Btn>
            </>
          )}
          {notice && !error && (
            <div style={{
              color: "var(--pos)", background: "var(--pos-soft)",
              padding: "8px 12px", borderRadius: 6, fontSize: 12.5, marginTop: 12,
            }}>{notice}</div>
          )}
          {error && (
            <div style={{
              color: "var(--neg)", background: "var(--neg-soft)",
              padding: "8px 12px", borderRadius: 6, fontSize: 12.5, marginTop: 12,
            }}>{error}</div>
          )}
        </div>
        <div style={{
          color: "var(--ink-3)", fontSize: 11.5, textAlign: "center",
          marginTop: 18, lineHeight: 1.55,
        }}>
          New members are invited by your firm's admin — Settings → Team.
        </div>
      </div>
    </div>
  );
}
